const CALLBACK_NAME = '__spondaGoogleMapsApiOnLoadCallback'
let loadPromise = null
let requestedLibraries = []

/**
 * Get the configured Google Maps browser API key.
 */
export function getGoogleMapsApiKey() {
  return window.google_api_key && window.google_api_key.GOOGLE_MAPS_API_KEY
}

/**
 * Create a custom image marker without requiring a cloud map ID.
 *
 * @param {object} googleMaps Google Maps API object.
 * @param {object} options Marker options.
 */
export function createGoogleMapsImageMarker( googleMaps, options ) {
  /**
   * Custom image marker backed by a Google Maps overlay.
   */
  class ImageMarker extends googleMaps.OverlayView {
    /**
     * Construct an image marker.
     *
     * @param {object} markerOptions Marker options.
     */
    constructor( markerOptions ) {
      super()

      this.position = markerOptions.position
      this.href = markerOptions.href
      this.resultId = markerOptions.resultId
      this.resultRef = markerOptions.resultRef
      this.src = markerOptions.src
      this.size = markerOptions.size || 36
      this.title = markerOptions.title || ''
      this.listeners = {}
      this.element = null

      this.setMap( markerOptions.map )
    }

    /**
     * Add marker to map pane.
     */
    onAdd() {
      const element = document.createElement( 'button' )
      const image = createGoogleMapsMarkerImage( this.src, this.size )

      element.type = 'button'
      element.style.position = 'absolute'
      element.style.padding = '0'
      element.style.border = '0'
      element.style.background = 'transparent'
      element.style.cursor = 'pointer'

      if ( this.title ) {
        element.title = this.title
        image.alt = this.title
      }

      element.appendChild( image )
      element.addEventListener( 'click', ( event ) => this.trigger( 'click', event ))
      this.element = element

      this.getPanes().overlayMouseTarget.appendChild( element )
    }

    /**
     * Draw marker on map.
     */
    draw() {
      if ( !this.element ) {
        return
      }

      const point = this.getProjection().fromLatLngToDivPixel( this.position )
      this.element.style.left = `${point.x - this.size / 2}px`
      this.element.style.top = `${point.y - this.size}px`
    }

    /**
     * Remove marker from map pane.
     */
    onRemove() {
      if ( this.element && this.element.parentNode ) {
        this.element.parentNode.removeChild( this.element )
      }

      this.element = null
    }

    /**
     * Register a marker event listener.
     *
     * @param {string} eventName Event name.
     * @param {Function} listener Event listener.
     */
    addListener( eventName, listener ) {
      if ( !this.listeners[eventName]) {
        this.listeners[eventName] = []
      }

      this.listeners[eventName].push( listener )

      return {
        remove: () => {
          this.listeners[eventName] = this.listeners[eventName].filter(
            ( savedListener ) => savedListener !== listener
          )
        }
      }
    }

    /**
     * Trigger marker listeners.
     *
     * @param {string} eventName Event name.
     * @param {Event} event Browser event.
     */
    trigger( eventName, event ) {
      const listeners = this.listeners[eventName] || []
      listeners.forEach(( listener ) => listener( event ))
    }
  }

  return new ImageMarker( options )
}

/**
 * Create image content for a custom marker.
 *
 * @param {string} src Image source.
 * @param {number} size Image size in pixels.
 */
function createGoogleMapsMarkerImage( src, size ) {
  const image = document.createElement( 'img' )
  image.src = src
  image.width = size
  image.height = size
  image.alt = ''
  image.style.display = 'block'
  image.style.width = `${size}px`
  image.style.height = `${size}px`

  return image
}

/**
 * Load the Google Maps API script once.
 *
 * @param {object} options Loader options.
 */
export default function loadGoogleMaps( options = {}) {
  const key = options.key || getGoogleMapsApiKey()
  const libraries = options.libraries || []

  requestedLibraries = requestedLibraries.concat(
    libraries.filter(( library ) => requestedLibraries.indexOf( library ) === -1 )
  )

  if ( window.google && window.google.maps ) {
    return importLibraries( libraries )
  }

  if ( !loadPromise ) {
    loadPromise = new Promise(( resolve, reject ) => {
      window[CALLBACK_NAME] = () => {
        delete window[CALLBACK_NAME]
        importLibraries( requestedLibraries )
          .then( resolve )
          .catch( reject )
      }

      const script = document.createElement( 'script' )
      script.async = true
      script.src = getScriptUrl( key, requestedLibraries )
      script.onerror = () => {
        delete window[CALLBACK_NAME]
        loadPromise = null
        reject( new Error( 'Could not load the Google Maps API' ))
      }

      document.head.appendChild( script )
    })
  }

  return loadPromise.then(() => importLibraries( libraries ))
}

/**
 * Build the Google Maps API script URL.
 *
 * @param {string} key API key.
 * @param {Array<string>} libraries API libraries.
 */
function getScriptUrl( key, libraries ) {
  const params = [
    `key=${encodeURIComponent( key )}`,
    `callback=${CALLBACK_NAME}`,
    'loading=async',
    'v=weekly'
  ]

  if ( libraries.length ) {
    params.push( `libraries=${libraries.map( encodeURIComponent ).join( ',' )}` )
  }

  return `https://maps.googleapis.com/maps/api/js?${params.join( '&' )}`
}

/**
 * Ensure requested Google Maps libraries are available.
 *
 * @param {Array<string>} libraries API libraries.
 */
function importLibraries( libraries ) {
  if ( !libraries.length || !window.google.maps.importLibrary ) {
    return Promise.resolve( window.google.maps )
  }

  return Promise.all(
    libraries.map(( library ) => window.google.maps.importLibrary( library ))
  ).then(() => window.google.maps )
}
